import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, Send, X } from "lucide-react";
import { API, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/i18n/LocaleContext";

const ALLOWED_EXT = ["dxf", "step", "stp", "iges", "igs", "dwg", "pdf", "zip", "jpg", "jpeg", "png"];

const inputCls = "bg-white border-[#D9E0E6] text-[var(--ci-grey)] placeholder:text-[var(--ci-grey)] focus-visible:ring-2 focus-visible:ring-[var(--ci-turquoise)] focus-visible:ring-offset-0 focus-visible:border-[var(--ci-turquoise)] rounded-xl h-12";
const selectTriggerCls = "bg-white border-[#D9E0E6] text-[var(--ci-grey)] rounded-xl h-12 focus:ring-2 focus:ring-[var(--ci-turquoise)] focus:ring-offset-0 data-[placeholder]:text-[var(--ci-grey)]";

export default function InquiryPortal() {
  const { t, dict, locale } = useT();
  const L = dict.inquiry.labels;
  const [form, setForm] = useState({ firma: "", ansprechpartner: "", email: "", telefon: "", losgroesse: "", nachricht: "" });
  const [bearbeitungsart, setBearbeitungsart] = useState("");
  const [material, setMaterial] = useState("");
  const [materialstaerke, setMaterialstaerke] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [successId, setSuccessId] = useState(null);
  const fileInputRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const addFiles = (list) => {
    const valid = [];
    for (const f of Array.from(list)) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(locale === "en" ? `File type .${ext} is not supported` : `Dateityp .${ext} wird nicht unterstützt`, { description: locale === "en" ? "Allowed: DXF, STEP, IGES, DWG, PDF, ZIP" : "Erlaubt: DXF, STEP, IGES, DWG, PDF, ZIP" });
        continue;
      }
      if (f.size > 50 * 1024 * 1024) {
        toast.error(locale === "en" ? `${f.name} is larger than 50 MB` : `${f.name} ist größer als 50 MB`);
        continue;
      }
      valid.push(f);
    }
    setFiles((prev) => [...prev, ...valid].slice(0, 8));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!bearbeitungsart || !material || !materialstaerke) {
      toast.error(t("inquiry.toastMissing"));
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      Object.entries({ ...form, bearbeitungsart, material, materialstaerke }).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("dateien", f));
      const res = await axios.post(`${API}/anfragen`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccessId(res.data.id);
      // Google Ads Conversion – nur nach erfolgreichem Server-Response feuern
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "ads_conversion_Kontakt_1", {
          send_to: "AW-18422670382",
          transaction_id: res.data.id,
        });
      }
      toast.success(t("inquiry.toastSuccess"), { description: t("inquiry.toastSuccessDesc") });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSuccessId(null);
    setForm({ firma: "", ansprechpartner: "", email: "", telefon: "", losgroesse: "", nachricht: "" });
    setBearbeitungsart(""); setMaterial(""); setMaterialstaerke(""); setFiles([]);
  };

  return (
    <section id="anfrage" className="py-32 lg:py-44 bg-[#F7F9FA] border-y border-[#E3E8EC]" data-testid="inquiry-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("inquiry.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-[var(--ci-grey)]">
            {t("inquiry.title1")}<span className="text-[var(--ci-blue)]">{t("inquiry.title2")}</span>
          </h2>
          <p className="mt-7 text-base text-[var(--ci-grey)] leading-relaxed max-w-md">
            {t("inquiry.copy")}
          </p>
          <ol className="mt-12 space-y-8">
            {dict.inquiry.steps.map(([nr, title, desc]) => (
              <li key={nr} className="flex gap-5">
                <span className="w-9 h-9 rounded-full bg-[var(--ci-blue-light)] text-[var(--ci-blue)] font-tech text-xs flex items-center justify-center shrink-0">{nr}</span>
                <div>
                  <div className="font-semibold text-[var(--ci-grey)]">{title}</div>
                  <div className="text-sm text-[var(--ci-grey)] mt-1.5">{desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          {successId ? (
            <div className="border border-[var(--ci-blue)]/30 bg-white rounded-2xl p-10 lg:p-14 shadow-[0_8px_48px_rgba(0,91,234,0.10)]" data-testid="inquiry-success">
              <CheckCircle2 size={40} className="text-[#10B981]" />
              <h3 className="text-2xl font-bold mt-6 text-[var(--ci-grey)]">{t("inquiry.success.title")}</h3>
              <p className="text-[var(--ci-grey)] mt-3 leading-relaxed">
                {t("inquiry.success.body")}
              </p>
              <div className="mt-7 inline-block border border-[#E3E8EC] bg-[#F7F9FA] rounded-xl px-5 py-3">
                <span className="font-tech text-[10px] tracking-[0.25em] uppercase text-[var(--ci-grey)] block">{t("inquiry.success.ticket")}</span>
                <span className="font-tech text-lg text-[var(--ci-grey)]" data-testid="inquiry-ticket-id">HEP-{successId.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="mt-9">
                <button onClick={reset} data-testid="inquiry-new-btn"
                  className="px-7 h-12 rounded-full border border-[#E3E8EC] hover:border-[var(--ci-turquoise)] text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] font-tech text-[11px] tracking-[0.2em] uppercase transition-colors duration-200">
                  {t("inquiry.success.again")}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-[#E3E8EC] bg-white rounded-2xl p-8 lg:p-12 shadow-[0_4px_40px_rgba(17,24,39,0.06)]" data-testid="inquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firma" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.firma}</Label>
                  <Input id="firma" required value={form.firma} onChange={set("firma")} placeholder={L.firmaPh} className={`${inputCls} mt-2.5`} data-testid="inquiry-firma-input" />
                </div>
                <div>
                  <Label htmlFor="ansprechpartner" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.name}</Label>
                  <Input id="ansprechpartner" required value={form.ansprechpartner} onChange={set("ansprechpartner")} placeholder={L.namePh} className={`${inputCls} mt-2.5`} data-testid="inquiry-name-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.email}</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder={L.emailPh} className={`${inputCls} mt-2.5`} data-testid="inquiry-email-input" />
                </div>
                <div>
                  <Label htmlFor="telefon" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.phone}</Label>
                  <Input id="telefon" value={form.telefon} onChange={set("telefon")} placeholder={L.phonePh} className={`${inputCls} mt-2.5`} data-testid="inquiry-telefon-input" />
                </div>
                <div>
                  <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.bearbeitung}</Label>
                  <Select value={bearbeitungsart} onValueChange={setBearbeitungsart}>
                    <SelectTrigger className={`${selectTriggerCls} mt-2.5`} data-testid="inquiry-bearbeitungsart-select"><SelectValue placeholder={L.placeholder} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                      {dict.inquiry.options.bearbeitung.map((o) => <SelectItem key={o} value={o} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.material}</Label>
                  <Select value={material} onValueChange={setMaterial}>
                    <SelectTrigger className={`${selectTriggerCls} mt-2.5`} data-testid="inquiry-material-select"><SelectValue placeholder={L.placeholder} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                      {dict.inquiry.options.material.map((o) => <SelectItem key={o} value={o} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.staerke}</Label>
                  <Select value={materialstaerke} onValueChange={setMaterialstaerke}>
                    <SelectTrigger className={`${selectTriggerCls} mt-2.5`} data-testid="inquiry-materialstaerke-select"><SelectValue placeholder={L.placeholder} /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E3E8EC] text-[var(--ci-grey)]">
                      {dict.inquiry.options.staerke.map((o) => <SelectItem key={o} value={o} className="focus:bg-[var(--ci-blue-light)] focus:text-[var(--ci-grey)]">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="losgroesse" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.lot}</Label>
                  <Input id="losgroesse" value={form.losgroesse} onChange={set("losgroesse")} placeholder={L.lotPh} className={`${inputCls} mt-2.5`} data-testid="inquiry-losgroesse-input" />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="nachricht" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.message}</Label>
                <Textarea id="nachricht" value={form.nachricht} onChange={set("nachricht")} rows={4}
                  placeholder={L.messagePh}
                  className="bg-white border-[#D9E0E6] text-[var(--ci-grey)] placeholder:text-[var(--ci-grey)] focus-visible:ring-2 focus-visible:ring-[var(--ci-blue)] focus-visible:ring-offset-0 rounded-xl mt-2.5"
                  data-testid="inquiry-nachricht-textarea" />
              </div>

              <div className="mt-7">
                <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">{L.cad}</Label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                  className={`mt-2.5 border-2 border-dashed rounded-2xl px-6 py-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors duration-200 ${dragOver ? "border-[var(--ci-blue)] bg-[var(--ci-blue-light)]" : "border-[#C9D4DD] hover:border-[var(--ci-turquoise)] bg-white"}`}
                  data-testid="inquiry-dropzone"
                >
                  <FileUp size={28} className={dragOver ? "text-[var(--ci-blue)]" : "text-[var(--ci-grey)]"} />
                  <p className="mt-4 text-sm text-[var(--ci-grey)]">{L.dropzone} <span className="text-[var(--ci-blue)] underline underline-offset-4">{L.browse}</span></p>
                  <p className="mt-2 font-tech text-[10px] tracking-[0.15em] uppercase text-[var(--ci-grey)]">{L.dropzoneNote}</p>
                  <input ref={fileInputRef} type="file" multiple hidden accept=".dxf,.step,.stp,.iges,.igs,.dwg,.pdf,.zip,.jpg,.jpeg,.png"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="inquiry-file-input" />
                </div>
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="inquiry-file-list">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-[#E3E8EC] bg-[#F7F9FA] rounded-lg px-4 py-2.5">
                        <span className="font-tech text-xs text-[var(--ci-grey)] truncate">{f.name} <span className="text-[var(--ci-grey)]">({(f.size / 1024).toFixed(0)} KB)</span></span>
                        <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                          className="text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors" data-testid={`inquiry-file-remove-${i}`} aria-label={locale === "en" ? `Remove file ${f.name}` : `Datei ${f.name} entfernen`}>
                          <X size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button type="submit" disabled={sending} data-testid="inquiry-submit-btn"
                className="mt-9 w-full flex items-center justify-center gap-3 h-14 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] disabled:opacity-60 text-white font-tech text-xs tracking-[0.22em] uppercase transition-all duration-200 active:scale-[0.99]">
                {sending ? (<><Loader2 size={16} className="animate-spin" /> {L.sending}</>) : (<><Send size={16} /> {L.submit}</>)}
              </button>
              <p className="mt-5 text-xs text-[var(--ci-grey)] leading-relaxed">
                {L.privacy}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
